// Two halves of the same problem — see utils/staleBuild.js for why it happens.
//
// StaleBuildBoundary  — a React error boundary. Catches a React.lazy() chunk that 404s during
//                       render. Without it the whole route unmounts to a blank white page,
//                       which is what a user saw if they navigated after a deploy.
// StaleBuildNotice    — a banner for the failures a boundary can NEVER see: `await import(...)`
//                       inside an async handler (plotly, jSPDF, duckdb-wasm). The app is still
//                       usable in that case, so this must not take over the page.
//
// Neither reloads on its own. See utils/staleBuild.js.
import React from "react";
import { Button } from "@mui/material";
import { isChunkLoadError, notifyStaleBuild, onStaleBuild, reloadToLatestBuild } from "utils/staleBuild";

const panelSx = {
  maxWidth: 520,
  margin: "18vh auto 0",
  padding: "28px 32px",
  borderRadius: 12,
  background: "#ffffff",
  color: "#0f172a",
  boxShadow: "0 18px 48px rgba(15,23,42,0.28)",
  textAlign: "center",
};

export class StaleBuildBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { stale: false, other: null };
  }

  static getDerivedStateFromError(error) {
    // A chunk 404 is the expected-after-deploy case and gets its own message. Anything else
    // is a real bug and must NOT be dressed up as "please reload" — that would hide crashes
    // behind a reassuring banner and train users to refresh past them.
    if (isChunkLoadError(error)) return { stale: true };
    return { other: error };
  }

  componentDidCatch(error) {
    if (!isChunkLoadError(error)) console.error("Unhandled render error:", error);
  }

  render() {
    const { stale, other } = this.state;
    if (stale) {
      return (
        <div style={panelSx}>
          <h2 style={{ margin: "0 0 8px", fontSize: 18 }}>A new version is available</h2>
          <p style={{ margin: "0 0 20px", fontSize: 14, color: "#475569", lineHeight: 1.5 }}>
            This page was updated while you had it open, so part of it could not load.
            Reloading picks up the new version.
          </p>
          <Button variant="contained" onClick={reloadToLatestBuild}>Reload</Button>
        </div>
      );
    }
    if (other) {
      return (
        <div style={panelSx}>
          <h2 style={{ margin: "0 0 8px", fontSize: 18 }}>Something went wrong</h2>
          <p style={{ margin: "0 0 20px", fontSize: 14, color: "#475569", lineHeight: 1.5 }}>
            {other.message || "An unexpected error occurred."}
          </p>
          <Button variant="contained" onClick={reloadToLatestBuild}>Reload</Button>
        </div>
      );
    }
    return this.props.children;
  }
}

export const StaleBuildNotice = () => {
  const [stale, setStale] = React.useState(false);
  React.useEffect(() => onStaleBuild(() => setStale(true)), []);
  if (!stale) return null;
  return (
    <div
      role="status"
      style={{
        position: "fixed", bottom: 18, left: "50%", transform: "translateX(-50%)",
        zIndex: 4000, display: "flex", alignItems: "center", gap: 14,
        background: "#0f172a", color: "#fff", fontSize: 13,
        padding: "10px 12px 10px 18px", borderRadius: 999,
        boxShadow: "0 12px 34px rgba(15,23,42,0.4)",
      }}
    >
      <span>A new version is available — reload to finish loading this page.</span>
      <Button size="small" variant="contained" onClick={reloadToLatestBuild} sx={{ textTransform: "none" }}>
        Reload
      </Button>
      <span
        role="button" tabIndex={0}
        onClick={() => setStale(false)}
        onKeyDown={(e) => e.key === "Enter" && setStale(false)}
        style={{ cursor: "pointer", color: "#94a3b8", padding: "0 4px" }}
        aria-label="Dismiss"
      >
        ✕
      </span>
    </div>
  );
};

export { notifyStaleBuild };
